import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import apiClient from '@/api/client';

const SendMessageModal = ({ isOpen, onClose }) => {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  
  // Voice recording states
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  // Start voice recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= 60) {
            stopRecording();
            return 60;
          }
          return prev + 1;
        });
      }, 1000);
      
      toast.info('🎤 Recording started...');
    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast.error('Microphone access denied. Please allow microphone access.');
    }
  };

  // Stop voice recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      toast.success('🎤 Recording stopped');
    }
  };

  // Delete recorded audio
  const deleteRecording = () => {
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingTime(0);
    toast.info('Recording deleted');
  };

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSend = async () => {
    // Validation - need either text message or audio
    if (!message.trim() && !audioBlob) {
      toast.error('Please enter a message or record a voice message');
      return;
    }

    if (message.trim() && message.trim().length < 10 && !audioBlob) {
      toast.error('Text message must be at least 10 characters long');
      return;
    }

    if (message.length > 1000) {
      toast.error('Message cannot exceed 1000 characters');
      return;
    }

    setSending(true);
    try {
      // If there's audio, send as FormData
      if (audioBlob) {
        const formData = new FormData();
        formData.append('message', message.trim() || '[Voice Message]');
        formData.append('audio', audioBlob, 'voice_message.webm');
        formData.append('has_audio', 'true');
        
        const response = await apiClient.post('/student/send-message', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        toast.success(response.data.message || 'Message sent successfully!');
      } else {
        // Text only
        const response = await apiClient.post('/student/send-message', {
          message: message.trim()
        });
        toast.success(response.data.message || 'Message sent successfully!');
      }
      
      setMessage('');
      setAudioBlob(null);
      setAudioUrl(null);
      setRecordingTime(0);
      onClose();
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to send message. Please try again.';
      toast.error(errorMsg);
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    if (!sending) {
      // Stop recording if active
      if (isRecording) {
        stopRecording();
      }
      setMessage('');
      setAudioBlob(null);
      setAudioUrl(null);
      setRecordingTime(0);
      onClose();
    }
  };

  const charCount = message.length;
  const charCountColor = charCount > 900 ? 'text-red-600' : charCount > 800 ? 'text-amber-600' : 'text-gray-500';

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent flex items-center gap-2">
            <span className="text-2xl">💬</span>
            অভিযোগ ও পরামর্শ
          </DialogTitle>
          <p className="text-sm text-gray-600 mt-1">আপনার অভিযোগ বা পরামর্শ জানান</p>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="message" className="text-base font-semibold">
              Your Message <span className="text-red-500">*</span>
            </Label>
            
            {/* Text area with mic button */}
            <div className="relative">
              <Textarea
                id="message"
                placeholder="Type your message here... (e.g., inquiry about mock tests, request for practice materials, schedule queries, etc.)"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="min-h-[150px] resize-y pr-14"
                maxLength={1000}
                disabled={sending || isRecording}
              />
              
              {/* Mic button */}
              <button
                type="button"
                onClick={isRecording ? stopRecording : startRecording}
                disabled={sending || audioBlob}
                className={`absolute right-3 top-3 p-2 rounded-full transition-all ${
                  isRecording 
                    ? 'bg-red-500 text-white animate-pulse' 
                    : audioBlob 
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:from-blue-600 hover:to-purple-600'
                }`}
                title={isRecording ? 'Stop Recording' : 'Record Voice Message'}
              >
                {isRecording ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <rect x="6" y="6" width="12" height="12" rx="2" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                    <line x1="12" x2="12" y1="19" y2="22"/>
                  </svg>
                )}
              </button>
            </div>
            
            <div className="flex justify-between items-center">
              <span className={`text-xs ${charCountColor} font-medium`}>
                {charCount} / 1000 characters
              </span>
              {isRecording && (
                <span className="text-xs text-red-500 font-medium animate-pulse">
                  🔴 Recording: {formatTime(recordingTime)} / 01:00
                </span>
              )}
            </div>
          </div>

          {/* Audio preview */}
          {audioUrl && !isRecording && (
            <div className="p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-3">
                <span className="text-2xl">🎤</span>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-700">Voice Message Recorded</p>
                  <p className="text-xs text-gray-500">Duration: {formatTime(recordingTime)}</p>
                  <audio src={audioUrl} controls className="w-full mt-2 h-8" />
                </div>
                <button
                  type="button"
                  onClick={deleteRecording}
                  className="p-2 text-red-500 hover:bg-red-100 rounded-full transition-colors"
                  title="Delete Recording"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 6h18"/>
                    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/>
                    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
                    <line x1="10" x2="10" y1="11" y2="17"/>
                    <line x1="14" x2="14" y1="11" y2="17"/>
                  </svg>
                </button>
              </div>
            </div>
          )}

          <div className="flex gap-3 justify-end pt-4 border-t">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={sending}
            >
              ✕ Cancel
            </Button>
            <Button
              onClick={handleSend}
              disabled={sending || isRecording || (!message.trim() && !audioBlob) || (message.trim() && message.trim().length < 10 && !audioBlob)}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            >
              {sending ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Sending...
                </>
              ) : (
                <>
                  📤 Send
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SendMessageModal;
